import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Col, Input, notification, Row, Typography } from 'antd';
import {
  Link,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Button } from 'components';
import { EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { useApi } from 'hooks/useApi';
import InvoicesCertificated from 'libs/api/invoices_certificateds';
import { useRecoilValue } from 'recoil';
import { getShoppingCenters } from 'hooks/useUser';
import { getShops } from 'hooks/useShops';
import { invoicesAdapter } from './invoices.adapter';
import UIHelper from '../../../../helpers/UIHelper';
import moment from 'moment';
import { useFilter } from '../../../../hooks/useFilter';
import uuid from 'react-uuid';
import { useNavigate } from '../../../../hooks/useNavigate';
import { statusColors } from '../InvoiceMonthlies/useInvoiceMonthlies';
const api = new InvoicesCertificated();

const useInvoices = (isMainPage, isBlank = false) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [sp] = useSearchParams();
  const { filterData } = useFilter();
  const [pageFilters, setPageFilters] = useState([]);
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const [editColumns, setEditColumns] = useState([]);
  const groupBy = sp.get('group_by');
  const includeCompany = sp.get('include_company');
  const [showModal, setShowModal] = useState(false);
  const blankFilters = useMemo(() => ({ blank_invoices: 'on' }), []);
  const { id } = useParams();
  const {
    data,
    updateInProcess,
    loading,
    onChangeTableFilter,
    handleDownloadExcel,
    onUpdate,
    singleItem,
    setLoading,
    searchParams,
    setUpdateInProcess,
    approveErrors,
    setApproveErrors,
  } = useApi({
    resourceKey: 'data',
    api,
    isMainPage,
    adapter: invoicesAdapter,
    aditionalFilters: isBlank ? blankFilters : undefined,
  });

  const onChangeValue = useCallback(
    (row) => {
      const { uuid } = row;
      return (e) => {
        const { name, value } = e.target;
        const newData = editColumns.map((item) => {
          if (item.uuid === uuid) {
            return {
              ...item,
              [name]: value,
            };
          }
          return item;
        });
        setEditColumns(newData);
      };
    },
    [editColumns],
  );

  const render = useCallback(
    (name) => {
      return (data, row) => {
        let keyPressCB = UIHelper.typeNumber;
        if (name === 'date') {
          return (
            <Typography>
              {moment(data, 'YYYY-MM-DD').format('YYYY-MMMM-DD')}
            </Typography>
          );
        }
        return (
          <Input
            onChange={onChangeValue(row)}
            name={name}
            onKeyPress={keyPressCB}
            value={data}
          />
        );
      };
    },
    [onChangeValue],
  );

  const handleAddNewRow = useCallback(() => {
    setEditColumns((s) => [
      ...s,
      {
        uuid: uuid(),
        taxable: 0,
        taxable_vat: 0,
        receipts: 0,
        pieces: 0,
      },
    ]);
  }, []);

  const columnsForEdit = useMemo(() => {
    return [
      {
        title: `${t('Date')}`,
        dataIndex: 'invoice_date',
        render: render('date'),
      },
      {
        title: `${t('Imponibile')} (€)`,
        dataIndex: 'taxable',
        render: render('taxable'),
      },
      {
        title: `${t('Imponibile + iva')} (€)`,
        dataIndex: 'taxable_vat',
        render: render('taxable_vat'),
      },
      {
        title: t('N.scontrini'),
        dataIndex: 'receipts',
        render: render('receipts'),
      },
      {
        title: t('N. pezzi'),
        dataIndex: 'pieces',
        render: render('pieces'),
      },
    ];
  }, [
    pathname,
    t,
    groupBy,
    includeCompany,
    render,
    handleAddNewRow,
    editColumns,
  ]);
  const statusTexts = useMemo(
    () => ({
      confirm: t('Confermato'),
      created: t('Nuovo'),
      reject: t('Rifiutato'),
    }),
    [],
  );
  const columns = useMemo(() => {
    return [
      {
        title: t('Date'),
        dataIndex: 'day',
        fixed: 'left',
        width: 180,
        sorter: () => {},
        render: (row) => {
          return moment(row, 'YYYY-MM-DD').locale('it').format('YYYY MMMM');
        },
      },
      {
        title: `${t('Imponibile')} (€)`,
        dataIndex: 'taxable_certificateds',
        sorter: () => {},
        render: (r) => `${r}`,
      },
      {
        title: `${t('Imponibile + iva')} (€)`,
        dataIndex: 'taxable_vat_certificateds',
        sorter: () => {},
        render: (r) => `${r}`,
      },
      {
        title: t('N.scontrini'),
        dataIndex: 'receipts_certificateds',
        sorter: () => {},
      },
      {
        title: t('N. pezzi'),
        dataIndex: 'pieces_certificateds',
        sorter: () => {},
      },
      {
        title: t('Stato'),
        dataIndex: 'status',
        render: (data) => (
          <Badge color={statusColors[data]} text={statusTexts[data]} />
        ),
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 120,
        fixed: 'right',
        render: (data, row) =>
          row.can_approve !== false && (
            <Row
              gutter={16}
              justify={'center'}
              onClick={(e) => e.stopPropagation()}
            >
              <Col>
                <Link
                  to={`${pathname}/${row.day}/edit?company_id=${sp.get(
                    'company_id',
                  )}`}
                >
                  <Button type={'secondary'} icon={<EditIcon />} />
                </Link>
              </Col>
            </Row>
          ),
      },
    ];
  }, [pathname, t, groupBy, includeCompany, statusTexts, sp]);
  const columnsDelta = useMemo(() => {
    return [
      {
        title: t('Date'),
        dataIndex: 'day',
        fixed: 'left',
        width: 180,
        sorter: () => {},
        render: (row) => {
          return moment(row, 'YYYY-MM-DD').locale('it').format('YYYY MMMM');
        },
      },
      {
        title: `${t('Imponibile')} (€)`,
        dataIndex: 'taxable_certificateds',
        sorter: () => {},
        render: (r) => `${r}`,
      },
      {
        title: `${t('Imponibile + iva')} (€)`,
        dataIndex: 'taxable_vat_certificateds',
        sorter: () => {},
        render: (r) => `${r}`,
      },
      {
        title: t('N.scontrini'),
        dataIndex: 'receipts_certificateds',
        sorter: () => {},
      },
      {
        title: t('N. pezzi'),
        dataIndex: 'pieces_certificateds',
        sorter: () => {},
      },
      {
        title: `${t('Delta Imponibile')}`,
        dataIndex: 'taxable_delta',
        sorter: () => {},
        render: (r) => `${r}`,
      },
      {
        title: `${t('Delta Imponibile + iva')}`,
        dataIndex: 'taxable_vat_delta',
        sorter: () => {},
        render: (r) => `${r}`,
      },
      {
        title: t('Delta N.scontrini'),
        dataIndex: 'receipts_delta',
        sorter: () => {},
      },
      {
        title: t('Delta N. pezzi'),
        dataIndex: 'pieces_delta',
        sorter: () => {},
      },
      {
        title: t('Stato'),
        dataIndex: 'status',
        render: (data) => (
          <Badge color={statusColors[data]} text={statusTexts[data]} />
        ),
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 120,
        fixed: 'right',
        render: (data, row) =>
          row.can_approve !== false && (
            <Row
              gutter={16}
              justify={'center'}
              onClick={(e) => e.stopPropagation()}
            >
              <Col>
                <Link
                  to={`${pathname}/${row.day}/edit?company_id=${sp.get(
                    'company_id',
                  )}`}
                >
                  <Button type={'secondary'} icon={<EditIcon />} />
                </Link>
              </Col>
            </Row>
          ),
      },
      // {
      //   title: '',
      //   dataIndex: 'address',
      //   align: 'right',
      //   width: 120,
      //   fixed: 'right',
      //   render: (data, row) =>
      //     row.can_update !== false && (
      //       <Row
      //         gutter={16}
      //         justify={'center'}
      //         onClick={(e) => e.stopPropagation()}
      //       >
      //         <Col>
      //           <Link to={`${pathname}/${row.id}/edit`}>
      //             <Button type={'secondary'} icon={<EditIcon />} />
      //           </Link>
      //         </Col>
      //       </Row>
      //     ),
      // },
    ];
  }, [pathname, t, groupBy, includeCompany, sp]);

  const filters = useMemo(
    () => [
      {
        title: t('Insegna'),
        type: 'select',
        name: 'company_id',
        values: filterData?.companies?.map((i) => ({
          value: i.value,
          title: i.title,
        })),
        inactiveValues: filterData?.inactive_companies?.map((i) => ({
          value: i.value,
          title: i.title,
        })),
      },
      {
        title: t('Date'),
        type: 'range',
        picker: 'month',
        name: 'by_month',
        keys: ['from', 'to'],
      },
      // {
      //   title: t('Stato'),
      //   type: 'select',
      //   name: 'status',
      //   values: filterData?.status?.map((i) => ({
      //     value: i.value,
      //     title: i.title,
      //   })),
      // },
    ],
    [t, shoppingCenters, filterData],
  );

  const onExportTo = async () => {
    setLoading(true);
    try {
      await api.export();
      setShowModal(false);
    } catch (e) {
      notification.error({
        message: e?.response?.data?.join(',') || 'Errore',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async (values, file, date) => {
    setUpdateInProcess(true);
    try {
      if (file) {
        const formData = new FormData();
        formData.append('company_id', values.company_id);
        formData.append('by_month', date.format('YYYY-MM-DD'));
        formData.append('file', file);
        const { data } = await api.parseExcel(formData);
        await api.create({
          data: {
            ...values,
            invoices_certificateds: data.data.map((i) => ({
              invoice_date: i.day,
              taxable: i.taxable,
              taxable_vat: i.taxable_vat,
              receipts: i.receipts,
              pieces: i.pieces,
            })),
          },
        });
        navigate(UIHelper.getListRoute(pathname, 1));
      } else {
        await api.create({
          data: {
            ...values,
            invoices_certificateds: editColumns.map((i) => ({
              invoice_date: i.invoice_date,
              taxable: i.taxable,
              taxable_vat: i.taxable_vat,
              receipts: i.receipts,
              pieces: i.pieces,
            })),
          },
        });
        navigate(UIHelper.getListRoute(pathname, 1));
      }
    } catch (e) {
      const errors = e.response?.data || [];
    } finally {
      setUpdateInProcess(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { filters },
        } = await api.filters();
        setPageFilters(filters);
      } catch (e) {
        console.log(e);
      }
    })();
  }, []);

  const handleApprove = async (values, paramKey, backCount) => {
    const data = {
      comment: values.description,
      company_id: +sp.get('company_id'),
      by_month: id,
    };
    switch (values.status) {
      case 'Approva': {
        data.confirmed = 'confirm';
        break;
      }
      case 'Rifiutato': {
        data.confirmed = 'reject';
        break;
      }
      default:
        break;
    }
    setUpdateInProcess(true);
    try {
      await api.approve(id, data);
      navigate(UIHelper.getListRoute(pathname, backCount));
    } catch (e) {
      console.log(e);
      setApproveErrors(
        (e?.response?.data?.errors &&
          e?.response?.data?.errors[0] &&
          e?.response?.data?.errors[0]?.messages) ||
          [],
      );
    } finally {
      setUpdateInProcess(false);
    }
  };

  return {
    columns,
    data,
    filters,
    updateInProcess,
    loading,
    onChangeTableFilter,
    singleItem,
    onExportTo,
    handleDownloadExcel,
    shops: filterData?.companies,
    inactiveShops: filterData?.inactive_companies || [],
    columnsForEdit,
    editColumns,
    handleCreateOrUpdate,
    handleAddNewRow,
    setEditColumns,
    columnsDelta,
    pageFilters,
    approveErrors,
    handleApprove,
    showModal,
    setShowModal,
  };
};

export default useInvoices;
